import React from 'react';
import PropTypes from 'prop-types';
import { del } from 'object-path-immutable';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faArrowRotateLeft } from '@fortawesome/free-solid-svg-icons/faArrowRotateLeft';
import { Button, DropdownMenu, Icon } from '@appcues/sonar';
import { useThemeEditor } from '@appcues/theme-editor';
import { Shape as LocalesShape } from 'ext/entities/locales';
import { EditorShape } from 'entities/step-groups/shape';
import { BlockContentShape } from 'entities/block/shape';
import CompiledThemeShape from 'entities/compiled-theme/shape';
import { mapOverrides, EDITOR_MAPPING } from 'lib/themes/themes';
import { donateComponent } from 'lib/themes/donate-component';
import { DEFAULT_LOCALE } from 'lib/localization';
import {
  Panel,
  Counter,
  Title,
  RotatingIcon,
  Overrides,
  SplitButton,
  ItemDescription,
} from './ThemeOverridesPanel.styled';

export const ThemeOverridesPanel = ({
  compiledTheme,
  type,
  editor,
  content,
  contentWithVariations,
  locales,
  onResetAll,
  onResetProperty,
}) => {
  const { setDonatedComponent } = useThemeEditor();

  const overrides = mapOverrides({
    compiledTheme,
    type: editor ? EDITOR_MAPPING : type,
    content: editor ?? contentWithVariations,
  });

  const handleResetProperty = paths => {
    const updatedChunk = paths.reduce(
      (acc, path) => del(acc, path),
      editor ?? contentWithVariations
    );
    onResetProperty(updatedChunk);
  };

  const handleDonate = isCreate => {
    setDonatedComponent(
      donateComponent({
        isCreate,
        compiledTheme,
        type: editor ? EDITOR_MAPPING : type,
        chunk: editor ?? content,
      })
    );
  };

  if (overrides.length === 0) return null;

  return (
    <Panel.Container>
      <Panel.Summary>
        <Counter>{overrides.length}</Counter>
        <Title>Theme overrides</Title>
        <RotatingIcon icon={faChevronUp} />
      </Panel.Summary>
      <Panel.Content>
        You're overriding the following properties:
        <Overrides.List>
          {overrides.map(({ key, paths, label, localeIds }) => (
            <Overrides.Row key={key}>
              <Overrides.Property>
                {label}
                {localeIds && (
                  <Overrides.PropertyLocales>
                    {`(${localeIds
                      .map(id => locales[id]?.name ?? DEFAULT_LOCALE.name)
                      .join(', ')})`}
                  </Overrides.PropertyLocales>
                )}
              </Overrides.Property>
              <Overrides.ResetButton
                variant="tertiary"
                iconOnly
                title={`Reset ${label} to theme`}
                onClick={() => handleResetProperty(paths)}
              >
                <Icon icon={faArrowRotateLeft} />
              </Overrides.ResetButton>
            </Overrides.Row>
          ))}
        </Overrides.List>
        <SplitButton>
          <Button fluid variant="secondary" onClick={onResetAll}>
            Reset instance
          </Button>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button iconOnly variant="secondary">
                <Icon icon={faChevronDown} />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content side="bottom" align="end">
                <DropdownMenu.Item onClick={() => handleDonate(true)}>
                  <DropdownMenu.ItemLabel>
                    Save as new
                    <ItemDescription>
                      Create a new variant to reuse with this theme.
                    </ItemDescription>
                  </DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
                <DropdownMenu.Item onClick={() => handleDonate(false)}>
                  <DropdownMenu.ItemLabel>
                    Update original
                    <ItemDescription>
                      Apply changes to this variant across all flows.
                    </ItemDescription>
                  </DropdownMenu.ItemLabel>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </SplitButton>
      </Panel.Content>
    </Panel.Container>
  );
};

ThemeOverridesPanel.propTypes = {
  compiledTheme: CompiledThemeShape,
  type: PropTypes.string,
  editor: EditorShape,
  content: BlockContentShape,
  contentWithVariations: BlockContentShape,
  locales: PropTypes.objectOf(LocalesShape),
  onResetAll: PropTypes.func,
  onResetProperty: PropTypes.func,
};
